'use client';

import { useState, useCallback, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import type { ComponentType } from 'react';

export interface NavigationNode {
  id: string;
  name: string;
  href?: string;
  icon?: ComponentType<{ className?: string }>;
  children?: NavigationNode[];
  requiredRole?: string;
  feature?: string;
}

export interface NavigationTreeProps {
  data: NavigationNode[];
  className?: string;
  onNavigate?: (href: string) => void;
}

// Helper function to extract common path prefix from hrefs
// e.g., ["/products/valuations/dashboard", "/products/valuations/valuations"] -> "/products/valuations/"
function getCommonPathPrefix(hrefs: string[]): string | null {
  if (hrefs.length === 0) return null;

  // Find common prefix by comparing first and last href (when sorted)
  const sorted = [...hrefs].sort();
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  let i = 0;
  while (i < first.length && first[i] === last[i]) {
    i++;
  }

  const commonPrefix = first.substring(0, i);

  // Ensure we end at a slash to get a valid path prefix
  const lastSlashIndex = commonPrefix.lastIndexOf('/');
  if (lastSlashIndex > 0) {
    return commonPrefix.substring(0, lastSlashIndex + 1);
  }

  return null;
}

// Recursive function to find all parent node IDs that contain the current page
function findParentNodes(
  nodes: NavigationNode[],
  currentPath: string,
  parentIds: string[] = []
): Set<string> {
  for (const node of nodes) {
    if (node.href === currentPath) {
      // Found the current page, return accumulated parent IDs as Set
      return new Set(parentIds);
    }

    if (node.children && node.children.length > 0) {
      // Recursively check children, adding current node ID to parent path
      const result = findParentNodes(node.children, currentPath, [...parentIds, node.id]);

      // If found in children (result has items), return it
      if (result.size > 0) {
        return result;
      }

      // Fallback: Check if current path matches this parent's path prefix
      // This handles cases where the current page is filtered out of navigation
      // but we still want to expand its parent
      const childHrefs = node.children
        .map((child) => child.href)
        .filter((href): href is string => !!href);
      if (childHrefs.length > 0) {
        const pathPrefix = getCommonPathPrefix(childHrefs);
        if (pathPrefix && currentPath.startsWith(pathPrefix)) {
          return new Set([...parentIds, node.id]);
        }
      }
    }
  }

  // Not found in this branch, return empty set
  return new Set();
}

function NavigationTree({ data, className = '', onNavigate: onNavigateProp }: NavigationTreeProps) {
  const { darkMode } = useDarkMode();
  const pathname = usePathname();
  const router = useRouter();

  // Auto-expand all parent nodes that contain the current page (at any depth)
  const initialExpandedNodes = useMemo(() => findParentNodes(data, pathname), [data, pathname]);

  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(initialExpandedNodes);

  // Update expanded nodes when pathname or data changes
  // (adjust-state-during-render pattern, https://react.dev/learn/you-might-not-need-an-effect)
  // This handles cases where:
  // 1. Data loads after component mounts (initial mount with permissions loading)
  // 2. Permissions update and navigation tree is re-filtered
  // 3. Pathname changes
  const [prevNav, setPrevNav] = useState({ pathname, data });
  if (prevNav.pathname !== pathname || prevNav.data !== data) {
    setPrevNav({ pathname, data });
    const newExpandedNodes = findParentNodes(data, pathname);

    // Update expanded nodes if we found any parent nodes
    if (newExpandedNodes.size > 0) {
      setExpandedNodes((prev) => {
        // Check if any new nodes need to be added
        const hasNewNodes = Array.from(newExpandedNodes).some((id) => !prev.has(id));
        if (!hasNewNodes && prev.size > 0) {
          return prev; // No changes needed
        }

        // Merge with existing (preserve manually expanded nodes)
        const merged = new Set(prev);
        newExpandedNodes.forEach((id) => merged.add(id));
        return merged;
      });
    }
  }

  // Toggle expand/collapse
  const toggleExpand = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Handle navigation - use custom callback if provided, otherwise default router navigation
  const handleNavigate = useCallback(
    (href: string) => {
      if (onNavigateProp) {
        onNavigateProp(href);
      } else {
        router.push(href);
      }
    },
    [router, onNavigateProp]
  );

  return (
    <nav className={className}>
      {/* Tree */}
      <div className={`divide-y ${darkMode ? 'divide-white/10' : 'divide-gray-200'}`}>
        {data.map((node) => (
          <div key={node.id}>
            <TreeNodeComponent
              node={node}
              level={0}
              expanded={expandedNodes.has(node.id)}
              onExpandToggle={() => toggleExpand(node.id)}
              onNavigate={handleNavigate}
              isExpanded={(id) => expandedNodes.has(id)}
              onNodeExpandToggle={toggleExpand}
              currentPath={pathname}
            />
          </div>
        ))}
      </div>
    </nav>
  );
}

interface TreeNodeComponentProps {
  node: NavigationNode;
  level: number;
  expanded: boolean;
  onExpandToggle: () => void;
  onNavigate: (href: string) => void;
  isExpanded: (id: string) => boolean;
  onNodeExpandToggle: (id: string) => void;
  currentPath: string;
}

function TreeNodeComponent({
  node,
  level,
  expanded,
  onExpandToggle,
  onNavigate,
  isExpanded,
  onNodeExpandToggle,
  currentPath,
}: TreeNodeComponentProps) {
  const { darkMode } = useDarkMode();
  const hasChildren = node.children && node.children.length > 0;
  const isActive = node.href === currentPath;
  const Icon = node.icon;

  return (
    <div>
      {/* Node Row */}
      <div
        className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-colors ${
          isActive
            ? darkMode
              ? 'bg-blue-900/20 text-blue-400'
              : 'bg-blue-50/50 text-blue-800'
            : darkMode
              ? 'text-gray-300 hover:bg-white/5 hover:text-white'
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
        }`}
        style={{ paddingLeft: `${level * 1.5 + 0.75}rem` }}
        onClick={() => {
          if (hasChildren) {
            onExpandToggle();
          } else if (node.href) {
            onNavigate(node.href);
          }
        }}
      >
        {/* Icon */}
        {Icon && (
          <Icon
            className={`size-6 flex-shrink-0 [&>path]:stroke-[1] ${
              isActive
                ? darkMode
                  ? 'text-blue-400'
                  : 'text-blue-800'
                : darkMode
                  ? 'text-gray-400'
                  : 'text-gray-500'
            }`}
          />
        )}

        {/* Label */}
        <span
          className={`flex-1 truncate font-medium ${level === 0 ? 'text-xs' : 'text-[0.9rem]'} ${
            isActive
              ? darkMode
                ? 'text-blue-400'
                : 'text-blue-800'
              : darkMode
                ? 'text-gray-300'
                : 'text-gray-700'
          }`}
        >
          {node.name}
        </span>

        {/* Expand/Collapse Button */}
        {hasChildren && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onExpandToggle();
            }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            className={`flex-shrink-0 rounded p-0.5 transition-transform ${
              expanded ? 'rotate-90' : ''
            } ${
              darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ChevronRightIcon className="size-4" />
          </button>
        )}
      </div>

      {/* Children */}
      {hasChildren && expanded && (
        <div className="mt-1">
          {node.children!.map((child) => (
            <TreeNodeComponent
              key={child.id}
              node={child}
              level={level + 1}
              expanded={isExpanded(child.id)}
              onExpandToggle={() => onNodeExpandToggle(child.id)}
              onNavigate={onNavigate}
              isExpanded={isExpanded}
              onNodeExpandToggle={onNodeExpandToggle}
              currentPath={currentPath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Export NavigationTree
export default NavigationTree;
